import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axiosInstance from "../../../Config/axiosInstance";
import DocumentScannerSharpIcon from '@mui/icons-material/DocumentScannerSharp';
import EditNoteSharpIcon from '@mui/icons-material/Edit';
import IconButton from '@mui/material/IconButton';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, DialogContentText } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AddIcon from "@mui/icons-material/Add";
import BusinessIcon from '@mui/icons-material/Business';
import "./PaymentExternalResourceInvoice.css";
import DeleteIcon from '@mui/icons-material/Delete';
import { createPortal } from "react-dom";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { format } from "date-fns";
import { NumericFormat } from "react-number-format";

const PaymentExternalResourceInvoice = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
    const [itemToDelete, setItemToDelete] = useState(null);
    const [showToast, setShowToast] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [fieldErrors, setFieldErrors] = useState({});

    const [formData, setFormData] = useState({
        project_code: "",
        cost_id: "",
        supplier: "",
        invoice_number: "",
        invoice_date: "",
        bill_maturity: "",
        invoice_without_vat: "",
        supplier_vat: "21",
        total_invoice_with_vat: "",
        currency: "",
        exchange_rate: "",
        exchange_rate_value: "",
        invoice_without_vat_system: "",
        total_invoice_with_vat_system: "",
        remaining_payment_system: "",
        remaining_payment_system_display: "",
        medium_alert: "5",
        critical_alert: "1",
        remarks: "",
        month: "",
        year: "",
        payments_status: "Unpaid"
    });

    // Formatare suma 
    const formatNumber = (value, locale = "ro-RO") => {
        if (value === null || value === undefined || value === "") return "";
        const n = Number(value);
        if (Number.isNaN(n)) return "";
        return new Intl.NumberFormat(locale, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }).format(n);
    };

    // Funcție pentru formatarea datei în format RO (dd.mm.yyyy)
    const formatDateRO = (dateString) => {
        if (!dateString) return "-";
        
        try {
            const date = new Date(dateString);
            // Verificăm dacă data este validă
            if (isNaN(date.getTime())) return "-";
            
            const day = String(date.getDate()).padStart(2, '0');
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const year = date.getFullYear();
            
            return `${day}.${month}.${year}`;
        } catch (error) {
            console.error("Error formatting date:", error);
            return "-";
        }
    };

    // Suppliers
    const [suppliers, setSuppliers] = useState([]);
    useEffect(() => {
        axiosInstance
            .get("/suppliers/")
            .then((response) => {
                const supplierOptions = response.data.map((supplier) => ({
                    value: supplier.id,
                    label: supplier.name,
                }));
                setSuppliers(supplierOptions);
            })
            .catch((error) => console.error("Error fetching suppliers:", error));
    }, []);

    // Project Codes
    const [projectCodes, setProjectCodes] = useState([]);
    useEffect(() => {
        axiosInstance
            .get('/project-codes/')
            .then(response => {
                const formatted = response.data.map(pc => ({
                    value: pc.id,
                    label: `${pc.code} `
                }));
                setProjectCodes(formatted);
            })
            .catch(error => console.error("Error fetching project codes:", error));
    }, []);

    // Cost Elements
    const [costElements, setCostElements] = useState([]);

    useEffect(() => {
        const fetchCostElements = async () => {
            try {
                const [projectBudgetRes, budgetStructureRes] = await Promise.all([
                    axiosInstance.get('/project-budget/'),
                    axiosInstance.get('/budget-structure/')
                ]);

                const projectBudgets = projectBudgetRes.data;
                const budgetStructures = budgetStructureRes.data;

                const budgetStructureMap = budgetStructures.reduce((acc, bs) => {
                    acc[bs.id] = bs;
                    return acc;
                }, {});

                const updatedCostElements = projectBudgets.map((pb) => {
                    const matched = budgetStructureMap[pb.cost_element];
                    return {
                        value: pb.id,
                        label: `${matched?.cost_element_id || "Fără număr"} - ${matched?.cost_element_description || "Fără descriere"}`,
                        number: matched?.cost_element_id || "Fără număr",
                        description: matched?.cost_element_description || "Fără descriere"
                    };
                });

                setCostElements(updatedCostElements);
            } catch (error) {
                console.error("Eroare la încărcarea livrabilelor:", error);
            }
        };

        fetchCostElements();
    }, []);

    const [currencies, setCurrencies] = useState([]);
    const [exchangeRates, setExchangeRates] = useState([]);
    const [systemCurrencyId, setSystemCurrencyId] = useState(null);
    const [systemCurrencyLabel, setSystemCurrencyLabel] = useState("");

    useEffect(() => {
        const fetchCurrencyData = async () => {
            try {
                const [currenciesRes, exchangeRes, systemRes] = await Promise.all([
                    axiosInstance.get("/currencies/"),
                    axiosInstance.get("/exchange-rate/"),
                    axiosInstance.get("/system-currency/"),
                ]);

                const formattedCurrencies = (currenciesRes.data || []).map((currency) => ({
                    value: currency.id,
                    label: currency.abbreviation || currency.name,
                    abbreviation: currency.abbreviation,
                    name: currency.name,
                }));

                setCurrencies(formattedCurrencies);
                setExchangeRates(exchangeRes.data || []);

                if (Array.isArray(systemRes.data) && systemRes.data.length > 0) {
                    const currencyId = systemRes.data[0]?.currency ?? null;
                    setSystemCurrencyId(currencyId || null);

                    const systemCurrency = formattedCurrencies.find(
                        (currency) => Number(currency.value) === Number(currencyId)
                    );

                    setSystemCurrencyLabel(
                        systemCurrency?.abbreviation || systemCurrency?.name || "System"
                    );
                }
            } catch (error) {
                console.error("Error loading currency data:", error);
            }
        };

        fetchCurrencyData();
    }, []);

    const invoiceCurrencyLabel = useMemo(() => {
        const currency = currencies.find(
            (item) => Number(item.value) === Number(formData.currency)
        );

        return currency?.abbreviation || currency?.label || "";
    }, [currencies, formData.currency]);

    const getExchangeRateValue = useCallback(() => {
        if (!formData.currency || !systemCurrencyId) return null;

        if (Number(formData.currency) === Number(systemCurrencyId)) {
            return 1;
        }

        const directRateValue = Number(formData.exchange_rate_value);
        if (Number.isFinite(directRateValue) && directRateValue > 0) {
            return directRateValue;
        }

        const selectedRate = exchangeRates.find(
            (rate) => Number(rate.id) === Number(formData.exchange_rate)
        );

        const rateValue = Number(selectedRate?.value);
        return Number.isFinite(rateValue) && rateValue > 0 ? rateValue : null;
    }, [
        formData.currency,
        formData.exchange_rate,
        formData.exchange_rate_value,
        systemCurrencyId,
        exchangeRates,
    ]);

    const convertToSystemAmount = useCallback(
        (value) => {
            const numericValue = Number(value);
            const rateValue = getExchangeRateValue();

            if (!Number.isFinite(numericValue) || rateValue === null) return "";

            return (numericValue * rateValue).toFixed(2);
        },
        [getExchangeRateValue]
    );

    const formatMoney = useCallback((value, currencyLabel = "") => {
        if (value === null || value === undefined || value === "") return "-";

        const formattedValue = formatNumber(value);
        if (!formattedValue) return "-";

        return `${formattedValue}${currencyLabel ? ` ${currencyLabel}` : ""}`;
    }, []);

    const formatConvertedMoney = useCallback(
        (originalValue, systemValueFromApi = "") => {
            const original = formatMoney(originalValue, invoiceCurrencyLabel);

            const systemValue =
                systemValueFromApi !== null &&
                    systemValueFromApi !== undefined &&
                    systemValueFromApi !== ""
                    ? systemValueFromApi
                    : convertToSystemAmount(originalValue);

            if (!systemValue || original === "-") return original;

            return `${original} → ${formatMoney(systemValue, systemCurrencyLabel)}`;
        },
        [convertToSystemAmount, formatMoney, invoiceCurrencyLabel, systemCurrencyLabel]
    );

    // Detalii factura curenta
    useEffect(() => {
        if (id) {
            axiosInstance.get(`/external-resource-invoice/${id}/`)
                .then(response => {
                    const data = response.data;
                    setFormData({
                        project_code: data.project_code,
                        cost_id: data.cost_id,
                        supplier: data.supplier,
                        invoice_number: data.invoice_number,
                        invoice_date: data.invoice_date,
                        bill_maturity: data.bill_maturity,
                        invoice_without_vat: data.invoice_without_vat,
                        supplier_vat: data.supplier_vat,
                        total_invoice_with_vat: data.total_invoice_with_vat,
                        currency: data.currency || "",
                        exchange_rate: data.exchange_rate || "",
                        exchange_rate_value: data.exchange_rate_value || "",
                        invoice_without_vat_system: data.invoice_without_vat_system || "",
                        total_invoice_with_vat_system: data.total_invoice_with_vat_system || "",
                        remaining_payment_system: data.remaining_payment_system || "",
                        remaining_payment_system_display: data.remaining_payment_system_display || "",
                        medium_alert: data.medium_alert,
                        critical_alert: data.critical_alert,
                        remarks: data.remarks,
                        month: data.month,
                        year: data.year,
                        payments_status: data.payments_status,
                    });
                })
                .catch(error => console.error("Error loading invoice:", error));
        }
    }, [id]);

    const statusColors = {
        "Partially paid": '#ff9800',
        Paid: '#4caf50',
        Unpaid: '#f44336',
    };

    // Modal
    const [openModal, setOpenModal] = useState(false);

    const handleAddClick = () => {
        setFormDataPayment({
            invoice_id: id,
            invoice_number: formData.invoice_number || '',
            supplier_payment: '',
            invoice_pay_date: '',
            invoice_pay_date_display: '',
            remaining_payment: ''
        });
        setFieldErrors({});
        setOpenModal(true);
    };

    const [isEditMode, setIsEditMode] = useState(false);
    const [currentEditId, setCurrentEditId] = useState(null);

    const handleDeleteClick = (payment) => {
        setItemToDelete(payment);
        setOpenDeleteDialog(true);
    };

    const handleConfirmDelete = async () => {
        try {
            await axiosInstance.delete(`/payment-history-external-invoice/${itemToDelete.id}/`);
            fetchPaymentsForInvoice();
            setOpenDeleteDialog(false);
        } catch (error) {
            console.error('Error deleting payment:', error.response?.data || error.message);
        }
    };

    const [formDataPayment, setFormDataPayment] = useState({
        invoice_id: '',
        invoice_number: '',
        supplier_payment: '',
        invoice_pay_date: '',
        invoice_pay_date_display: '',
        remaining_payment: '',
    });

    // Handler pentru date picker
    const handleDatePick = (field, date) => {
        if (!date) {
            setFormDataPayment(prev => ({
                ...prev,
                [field]: "",
                [field + "_display"]: "",
            }));
            return;
        }

        const iso = format(date, "yyyy-MM-dd");
        const display = format(date, "dd.MM.yyyy");

        setFormDataPayment(prev => ({
            ...prev,
            [field]: iso,
            [field + "_display"]: display,
        }));

        // Clear field error when user selects a date
        if (fieldErrors.invoice_pay_date) {
            setFieldErrors(prev => ({ ...prev, invoice_pay_date: "" }));
        }
    };

    // Validate payment form
    const validatePaymentForm = () => {
        const newErrors = {};
        const remaining = parseFloat(remainingAmount) || 0;
        const supplierPayment = parseFloat(formDataPayment.supplier_payment) || 0;

        if (!formDataPayment.invoice_pay_date) {
            newErrors.invoice_pay_date = "Required";
        }

        if (!formDataPayment.supplier_payment || formDataPayment.supplier_payment === '') {
            newErrors.supplier_payment = "Required";
        } else if (supplierPayment <= 0) {
            newErrors.supplier_payment = "Supplier Payment must be greater than 0";
        } else if (supplierPayment > remaining) {
            newErrors.supplier_payment = `Payment exceeds remaining amount (${formatNumber(
                remaining
            )} ${invoiceCurrencyLabel || ""}).`;
        }

        setFieldErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async () => {
        if (!validatePaymentForm()) return;

        setIsSaving(true);

        try {
            const payload = {
                invoice: formDataPayment.invoice_id,
                supplier_payment: parseFloat(formDataPayment.supplier_payment),
                invoice_pay_date: formDataPayment.invoice_pay_date,
                remaining_payment: formDataPayment.remaining_payment || 0,
            };

            if (isEditMode && currentEditId) {
                await axiosInstance.put(`/payment-history-external-invoice/${currentEditId}/`, payload);
            } else {
                await axiosInstance.post('/payment-history-external-invoice/', payload);
            }

            setShowToast(true);
            fetchPaymentsForInvoice();

            // Închidem modalul DUPĂ ce toast-ul se afișează
            setTimeout(() => {
                handleCloseModal();
                setIsSaving(false);
                window.location.reload();
            }, 700);

        } catch (error) {
            console.error("Submit error:", error.response?.data || error.message);
            setIsSaving(false);
        }
    };

    // Funcția care închide modalul și resetează stările
    const handleCloseModal = () => {
        setOpenModal(false);
        setIsEditMode(false);
        setCurrentEditId(null);
        setWarningMessage('');
        setFieldErrors({});
        setFormDataPayment({
            invoice_id: '',
            supplier_payment: '',
            invoice_pay_date: '',
            invoice_pay_date_display: '',
            remaining_payment: ''
        });
    };

    const [invoicePayments, setInvoicePayments] = useState([]);

    const fetchPaymentsForInvoice = useCallback(async () => {
        if (!id) return;

        try {
            const response = await axiosInstance.get(`/payment-history-external-invoice/?invoice=${id}`);
            const payments = response.data;

            const filteredPayments = payments.filter(payment => payment.invoice === parseInt(id));

            const totalSupplierPayment = filteredPayments.reduce((total, payment) => {
                const amount = parseFloat(payment.supplier_payment);
                return !isNaN(amount) ? total + amount : total;
            }, 0);

            setTotalSupplierPayment(totalSupplierPayment);
            setInvoicePayments(filteredPayments);
        } catch (error) {
            console.error("Eroare la aducerea plăților:", error);
        }
    }, [id]);

    const [warningMessage, setWarningMessage] = useState('');
    // eslint-disable-next-line no-unused-vars
    const [totalSupplierPayment, setTotalSupplierPayment] = useState(0);

    const handleChange = (e) => {
        const { name, value } = e.target;
        const numericFields = ['supplier_payment', 'remaining_payment'];

        const updatedValue = numericFields.includes(name)
            ? (value === '' ? '' : parseFloat(value))
            : value;

        const updatedFormData = {
            ...formDataPayment,
            [name]: updatedValue,
        };

        if (name === 'supplier_payment') {
            const supplierPayment = updatedValue || 0;
            const totalInvoice = parseFloat(formData.total_invoice_with_vat) || 0;

            // Calculăm totalul plăților EXCLUZÂND plata curentă (dacă suntem în edit)
            let totalPaidExcludingCurrent = 0;

            invoicePayments.forEach(payment => {
                const amount = parseFloat(payment.supplier_payment);
                if (!isNaN(amount)) {
                    // Dacă suntem în modul edit și aceasta este plata curentă, nu o includem
                    if (isEditMode && currentEditId && payment.id === currentEditId) {
                        return; // skip this payment
                    }
                    totalPaidExcludingCurrent += amount;
                }
            });

            // Calculăm remaining-ul corect
            const remaining = totalInvoice - totalPaidExcludingCurrent - supplierPayment;

            updatedFormData.remaining_payment = remaining.toFixed(2);

            if (remaining < 0) {
                setWarningMessage("⚠️ Payment cannot exceed the total invoice amount.");
            } else if (remaining === 0) {
                setWarningMessage("");
            } else {
                setWarningMessage("");
            }
        }

        // Clear field error when user starts typing
        if (fieldErrors[name]) {
            setFieldErrors(prev => ({ ...prev, [name]: "" }));
        }

        setFormDataPayment(updatedFormData);
    };

    useEffect(() => {
        if (id) {
            fetchPaymentsForInvoice();
        }
    }, [id, fetchPaymentsForInvoice]);

    const handleEditClick = (payment) => {
        // Format the date for display if it exists
        const displayDate = payment.invoice_pay_date
            ? format(new Date(payment.invoice_pay_date), "dd.MM.yyyy")
            : "";

        setFormDataPayment({
            invoice_id: payment.invoice,
            supplier_payment: payment.supplier_payment,
            invoice_pay_date: payment.invoice_pay_date,
            invoice_pay_date_display: displayDate,
            remaining_payment: payment.remaining_payment
        });
        setCurrentEditId(payment.id);
        setIsEditMode(true);
        setFieldErrors({});
        setOpenModal(true);
    };

    // Funcția pentru a calcula suma totală a supplier_payment
    const calculateSupplierPaymentTotal = (payments) => {
        return payments.reduce((total, payment) => {
            const supplierPayment = parseFloat(payment.supplier_payment);
            return !isNaN(supplierPayment) ? total + supplierPayment : total;
        }, 0);
    };

    // Funcția care scade suma remaining_payment din total_invoice_with_vat
    const calculateRemainingAmount = (invoicePayments, totalInvoiceWithVat) => {
        const totalPaid = calculateSupplierPaymentTotal(invoicePayments);
        const totalInvoice = parseFloat(totalInvoiceWithVat) || 0;
        const remainingAmount = totalInvoice - totalPaid;
        return remainingAmount.toFixed(2);
    };

    const remainingAmount = calculateRemainingAmount(invoicePayments, formData.total_invoice_with_vat);
    const [canEditPayment, setCanEditPayment] = useState(false);
    const [canEditInvoice, setCanEditInvoice] = useState(false);
    const [canCreatePayment, setCanCreatePayment] = useState(false);
    const [canDeletePayment, setCanDeletePayment] = useState(false);

    useEffect(() => {
        const requiredPathInvoice = "/external-resource-invoice";
        const requiredPathPayment = "/external-resource-invoice/payment-external-resource-invoice";
        const storedPermissions = JSON.parse(localStorage.getItem("permissions"));
        const permissions = storedPermissions?.pages || [];

        setCanEditInvoice(permissions.some((page) => new URL(page.url).pathname === requiredPathInvoice && page.can_edit));
        setCanCreatePayment(permissions.some((page) => new URL(page.url).pathname === requiredPathPayment && page.can_create));
        setCanEditPayment(permissions.some((page) => new URL(page.url).pathname === requiredPathPayment && page.can_edit));
        setCanDeletePayment(permissions.some((page) => new URL(page.url).pathname === requiredPathPayment && page.can_delete));
    }, []);

    // Ascunde notificarea după 2 secunde
    useEffect(() => {
        if (showToast) {
            const timer = setTimeout(() => {
                setShowToast(false);
            }, 2000);
            return () => clearTimeout(timer);
        }
    }, [showToast]);

    // Stil pentru erori
    const errorStyle = {
        color: "#d32f2f",
        fontSize: "12px",
        marginTop: "2px",
        display: "block"
    };

    // Component pentru afișarea erorilor
    const ErrorMessage = ({ error }) => {
        if (!error) return null;
        return (
            <div style={errorStyle}>
                {error}
            </div>
        );
    };

    return (
        <div className="payment-ext-resource-invoice-page">
            {/* Success Toast */}
            {showToast &&
                createPortal(
                    <div className="global-toast-on-top">
                        {isEditMode ? "✔ Payment updated successfully!" : "✔ Payment added successfully!"}
                    </div>,
                    document.body
                )
            }

            {/* Modal pentru adaugare plata */}
            <Dialog
                open={openModal}
                onClose={handleCloseModal}
                maxWidth="sm"
                fullWidth
                className="add-payment-container"
            >
                <DialogTitle className="title-popup">
                    {isEditMode ? "Edit Payment" : "Add New Payment"}
                </DialogTitle>
                <DialogContent dividers className="payment-ext-resource-invoice-page">
                    {/* Invoice number (doar afișare) */}
                    <div className="form-group">
                        <label htmlFor="invoice" className="label">Invoice</label>
                        <input
                            type="text"
                            id="invoice"
                            name="invoice"
                            value={formData.invoice_number || ''}
                            className="input"
                            disabled
                        />
                    </div>

                    {/* Payment Date */}
                    <div className="form-group">
                        <label htmlFor="invoice_pay_date" className="label">Invoice Pay Date <span style={{ color: 'red' }}>*</span></label>

                        <div className="datepicker-container">
                            {/* Input vizibil */}
                            <input
                                type="text"
                                value={formDataPayment.invoice_pay_date_display || ''}
                                placeholder="dd.mm.yyyy"
                                className={`input ${fieldErrors.invoice_pay_date ? "input-error" : ""}`}
                                readOnly
                                onClick={() => document.querySelector("#invoice_pay_date_picker")?.focus()}
                                style={{ borderColor: fieldErrors.invoice_pay_date ? "#d32f2f" : "" }}
                            />

                            {/* DatePicker ascuns */}
                            <DatePicker
                                id="invoice_pay_date_picker"
                                selected={formDataPayment.invoice_pay_date ? new Date(formDataPayment.invoice_pay_date) : null}
                                onChange={(date) => handleDatePick("invoice_pay_date", date)}
                                dateFormat="dd.MM.yyyy"
                                className="datepicker-hidden"
                                autoComplete="off"
                                popperClassName="datepicker-popper-custom"
                                popperProps={{
                                    strategy: "fixed"
                                }}
                                calendarClassName="datepicker-calendar-custom"
                            />

                            {/* Iconița calendar */}
                            <span
                                className="calendar-icon-inside"
                                onClick={() => document.querySelector("#invoice_pay_date_picker")?.focus()}
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18"
                                    viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                                    <line x1="16" y1="2" x2="16" y2="6" />
                                    <line x1="8" y1="2" x2="8" y2="6" />
                                    <line x1="3" y1="10" x2="21" y2="10" />
                                </svg>
                            </span>
                        </div>

                        <ErrorMessage error={fieldErrors.invoice_pay_date} />
                    </div>

                    {/* Supplier Payment */}
                    <div className="form-group">
                        <label htmlFor="supplier_payment" className="label">
                            Supplier Payment{" "}
                            {invoiceCurrencyLabel ? `(${invoiceCurrencyLabel})` : ""}
                            <span style={{ color: 'red' }}> *</span>
                        </label>
                        <NumericFormat
                            id="supplier_payment"
                            name="supplier_payment"
                            value={formDataPayment.supplier_payment ?? ""}
                            onValueChange={(values) =>
                                handleChange({
                                    target: {
                                        name: "supplier_payment",
                                        value: values.floatValue ?? 0
                                    }
                                })
                            }
                            thousandSeparator="."
                            decimalSeparator=","
                            decimalScale={2}
                            fixedDecimalScale={false}
                            allowNegative={false}
                            allowLeadingZeros={false}
                            inputMode="decimal"
                            className="input"
                            placeholder="0,00"
                            style={{ borderColor: fieldErrors.supplier_payment ? "#d32f2f" : "" }}
                            required
                        />
                        <ErrorMessage error={fieldErrors.supplier_payment} />
                    </div>

                    <div className="form-group">
                        {warningMessage && (
                            <div style={{ color: 'red', marginTop: '0.5rem' }}>
                                {warningMessage}
                            </div>
                        )}
                    </div>

                </DialogContent>

                <DialogActions>
                    <Button onClick={handleCloseModal} color="secondary" className="button discard">
                        Discard
                    </Button>
                    <Button
                        onClick={handleSubmit}
                        color="primary"
                        className={`button save ${isSaving ? "disabled" : ""}`}
                        disabled={isSaving}
                    >
                        {isSaving ? "Saving..." : "Save"}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={openDeleteDialog}
                onClose={() => setOpenDeleteDialog(false)}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
            >
                <DialogTitle id="alert-dialog-title">Confirm Delete</DialogTitle>
                <DialogContent>
                    <DialogContentText id="alert-dialog-description">
                        Are you sure you want to delete this payment?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => setOpenDeleteDialog(false)}
                        sx={{
                            backgroundColor: '#e0e0e0',
                            color: '#424242',
                            '&:hover': {
                                backgroundColor: '#bdbdbd',
                            }
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleConfirmDelete}
                        sx={{
                            backgroundColor: '#d32f2f',
                            color: 'white',
                            '&:hover': {
                                backgroundColor: '#b71c1c',
                            }
                        }}
                        autoFocus
                    >
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>

            <div className="payment-back-container">
                <ArrowBackIcon className="back-arrow" onClick={() => navigate(-1)} />
                <h2 className="payment-back">Back</h2>
            </div>
            <hr className="title-separator" />
            <div className="payment-header-title">
                <DocumentScannerSharpIcon />
                <h2 className="payment-title">External resource invoice</h2>
            </div>
            <div className="container-invoice-status">
                <div className="payment-input-text">
                    {formData.invoice_number || "-"}
                </div>
                <div className="status-and-edit-group">
                    <div style={{
                        backgroundColor: statusColors[formData.payments_status] || '#ccc',
                        color: 'white',
                        padding: '5px 10px',
                        borderRadius: '10px',
                        width: '90px',
                        textAlign: 'center',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        height: '15px',
                        marginRight: '8px'
                    }}>
                        {formData.payments_status || "-"}
                    </div>
                    {canEditInvoice && (
                        <button className="edit-button" onClick={() => navigate(`/external-resource-invoice/edit-external-resource-invoice/${id}`)}>
                            <EditNoteSharpIcon style={{ fontSize: '18px' }} sx={{ color: '#1976d2' }} />
                            Edit Invoice
                        </button>
                    )}
                </div>
            </div>

            <div className="payment-client-container">
                <div className="header">
                    <div className="supplier-info">
                        <BusinessIcon />
                        {suppliers.find((s) => s.value === formData.supplier)?.label || ""}
                    </div>

                    {/* Buton vizibil DOAR pe ecrane mari */}
                    {canCreatePayment && (
                        <div className="add-payment-button-large">
                            <Button
                                className="custom-button"
                                variant="contained"
                                color="primary"
                                startIcon={<AddIcon className="add-icon" />}
                                onClick={handleAddClick}
                            >
                                Add New payment
                            </Button>
                        </div>
                    )}
                </div>
            </div>
            <hr className="supplier-separator" />
            <div className="invoice-container">
                <div className="invoice-card">
                    <div className="invoice-grid">
                        <div className="invoice-label">Project code</div>
                        <div className="invoice-value">{projectCodes.find((s) => s.value === formData.project_code)?.label || "-"}</div>

                        <div className="invoice-label">Cost ID</div>
                        <div className="invoice-value">{costElements.find((c) => c.value === formData.cost_id)?.number || "-"}</div>

                        <div className="invoice-label">Cost Description</div>
                        <div className="invoice-value">{costElements.find((c) => c.value === formData.cost_id)?.description || "-"}</div>

                        <div className="invoice-label">Invoice date</div>
                        <div className="invoice-value">{formatDateRO(formData.invoice_date)}</div>

                        <div className="invoice-label">Bill Maturity</div>
                        <div className="invoice-value">{formatDateRO(formData.bill_maturity)}</div>

                        <div className="invoice-label">Invoice without VAT</div>
                        <div className="invoice-value">
                            {formatConvertedMoney(
                                formData.invoice_without_vat,
                                formData.invoice_without_vat_system
                            )}
                        </div>

                        <div className="invoice-label">Supplier VAT (%)</div>
                        <div className="invoice-value">{formatNumber(formData.supplier_vat)} %</div>

                        <div className="invoice-label">Total invoice with VAT</div>
                        <div className="invoice-value">
                            {formatConvertedMoney(
                                formData.total_invoice_with_vat,
                                formData.total_invoice_with_vat_system
                            )}
                        </div>

                        <div className="invoice-label">Month</div>
                        <div className="invoice-value">{formData.month || "-"}</div>

                        <div className="invoice-label">Year</div>
                        <div className="invoice-value">{formData.year || "-"}</div>

                        <div className="invoice-label">Remarks</div>
                        <div className="invoice-value">{formData.remarks || "-"}</div>
                    </div>
                </div>

                {canCreatePayment && (
                    <div className="add-payment-button-small">
                        <Button
                            className="custom-button"
                            variant="contained"
                            color="primary"
                            startIcon={<AddIcon className="add-icon" />}
                            onClick={handleAddClick}
                        >
                            Add New payment
                        </Button>
                    </div>
                )}

                <div className="invoice-card">
                    <div className="payment-ext-resource-invoice-page">
                        {/* Tabelul cu plățile */}
                        {invoicePayments.length > 0 && (
                            <div className="invoice-card">
                                <div className="payment-grid">
                                    <table style={{ width: "100%", borderCollapse: "collapse" }}>
                                        <tbody>
                                            {[...invoicePayments]
                                                .sort((a, b) => a.id - b.id)
                                                .map((payment, index) => (
                                                    <tr key={index}>
                                                        <td style={{ fontWeight: "bold" }}>Payment</td>
                                                        <td>
                                                            {formatConvertedMoney(
                                                                payment.supplier_payment,
                                                                payment.supplier_payment_system_display ||
                                                                payment.supplier_payment_system ||
                                                                payment.amount_system_display ||
                                                                payment.amount_system ||
                                                                ""
                                                            )}
                                                        </td>
                                                        <td>{payment.invoice_pay_date ? format(new Date(payment.invoice_pay_date), "dd.MM.yyyy") : "-"}</td>
                                                        {(canEditPayment || canDeletePayment) && (
                                                            <td>
                                                                {canEditPayment && (
                                                                    <IconButton sx={{ color: '#1976d2' }} onClick={() => handleEditClick(payment)}>
                                                                        <EditNoteSharpIcon />
                                                                    </IconButton>
                                                                )}
                                                                {canDeletePayment && (
                                                                    <IconButton onClick={() => handleDeleteClick(payment)} sx={{ color: '#d32f2f' }}>
                                                                        <DeleteIcon />
                                                                    </IconButton>
                                                                )}
                                                            </td>
                                                        )}
                                                    </tr>
                                                ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}

                        {/* Remaining payment */}
                        <div className="invoice-card">
                            <div className="invoice-grid-payment">
                                <div className="payment-remaining">
                                    <strong>Remaining payment: </strong>{" "}
                                    {formatConvertedMoney(
                                        remainingAmount,
                                        formData.remaining_payment_system_display ||
                                        formData.remaining_payment_system
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PaymentExternalResourceInvoice;